import { animate, state, style, transition, trigger } from '@angular/animations';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  inject,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AssetDto, AssetVersionDto, AssetVideoTypeDataDto, GalleryDto } from '@nusign/picflow-backend-types';
import Hls, { Level, LevelParsed } from 'hls.js';
import { Observable, Subscription, map, of } from 'rxjs';
import { fadeUpIf } from '../../../animations/fade-up.if.animation';
import { fadeIf } from '../../../animations/fade.if.animation';
import { Rect } from '../../../classes/rect.class';
import { ScreenSizes } from '../../../classes/screen-sizes.class';
import { longPressDuration } from '../../../consts';
import { GROWTHBOOK } from '../../../growthbook';
import { devicePixelRatio } from '../../../lib/device-pixel-ratio';
import { isMetaKey } from '../../../lib/is-meta-key';
import { sleep } from '../../../lib/sleep';
import { throttle } from '../../../lib/throttle';
import { PICFLOW_ANALYTICS } from '../../../picflow-analytics';
import { IsArrayPipe } from '../../../pipes/is-array.pipe';
import { CommentService } from '../../../services/comment.service';
import { StateService } from '../../../services/state.service';
import { ToastService } from '../../../services/toast.service';
import { VideoHlsService } from '../../../services/video-hls.service';
import { VideoSettingsService } from '../../../services/video-settings.service';
import { VideoTimeService } from '../../../services/video-time.service';
import { AvatarComponent } from '../../../shared/components/avatar/avatar.component';
import { ContextMenuComponent } from '../../../shared/components/context-menu/context-menu.component';
import { SvgComponent } from '../../../shared/components/svg/svg.component';
import { ButtonDirective } from '../../../shared/directives/button.directive';
import { TooltipDirective } from '../../../shared/directives/tooltip.directive';
import { VideoResolutionTypePipe } from '../../../shared/pipes/video-resolution-type.pipe';
import { VideoResolutionPipe } from '../../../shared/pipes/video-resolution.pipe';
import { CommentThread } from '../comments/comments.component';
import { VideoThumbnailComponent } from '../video-thumbnail/video-thumbnail.component';

export interface VideoTimeData {
  durationSeconds: number;
  currentSecond: number;
  durationFrames: number;
  currentFrame: number;
}

@Component({
  selector: 'app-video-viewer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './video-viewer.component.html',
  styleUrls: ['./video-viewer.component.scss'],
  animations: [
    trigger('playPauseIndicator', [
      state('hidden', style({ opacity: 0, transform: 'translate(-50%, -50%) scale(1)' })),
      state('visible', style({ opacity: 1, transform: 'translate(-50%, -50%) scale(0.5)' })),
      transition('visible => hidden', [animate('0.5s ease-out')]),
    ]),
    trigger('timeSkipIndicator', [
      state('hidden_back', style({ opacity: 0, transform: 'translate(-50%, -50%) scale(1)' })),
      state('visible_back', style({ opacity: 1, transform: 'translate(-50%, -50%) scale(0.5)' })),
      state('hidden_forward', style({ opacity: 0, transform: 'translate(-50%, -50%) scale(1)' })),
      state('visible_forward', style({ opacity: 1, transform: 'translate(-50%, -50%) scale(0.5)' })),
      transition('visible_back => hidden_back', [animate('0.5s ease-out')]),
      transition('visible_forward => hidden_forward', [animate('0.5s ease-out')]),
    ]),
    trigger('controls', fadeUpIf),
    trigger('timeline', fadeIf),
    trigger('videoContainer', [transition(':enter', [])]),
    trigger('thumbnail', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translate(-50%, calc(-100% + 20px)) translateZ(0) scale(0.8)' }),
        animate('0.2s ease', style({ opacity: 1, transform: 'translate(-50%, -100%) translateZ(0)' })),
      ]),
      transition(':leave', [
        style({ opacity: 1, transform: 'translate(-50%, -100%)' }),
        animate(
          '0.2s ease',
          style({ opacity: 0, transform: 'translate(-50%, calc(-100% + 20px)) translateZ(0) scale(0.8)' })
        ),
      ]),
    ]),
    trigger('initialPlayButton', [
      transition(':enter', [style({ opacity: 0 }), animate('0.5s ease', style({ opacity: 1 }))]),
      transition(':leave', [
        style({ opacity: 1 }),
        animate('0.5s ease', style({ opacity: 0, transform: 'translate(-50%, -50%) scale(1.2)' })),
      ]),
    ]),
  ],
  imports: [
    FormsModule,
    ButtonDirective,
    SvgComponent,
    ContextMenuComponent,
    TooltipDirective,
    VideoResolutionTypePipe,
    VideoThumbnailComponent,
    DecimalPipe,
    AsyncPipe,
    IsArrayPipe,
    AvatarComponent,
    VideoResolutionPipe,
  ],
})
export class VideoViewerComponent implements OnInit, OnChanges, OnDestroy {
  private check = inject(ChangeDetectorRef);
  private videoHlsService = inject(VideoHlsService);
  private analytics = inject(PICFLOW_ANALYTICS);
  private zone = inject(NgZone);
  private state = inject(StateService);
  private videoSettingsService = inject(VideoSettingsService);
  private videoTime = inject(VideoTimeService);
  public growthBook = inject(GROWTHBOOK);
  public toast = inject(ToastService);
  hls: Hls;
  levels: LevelParsed[] = [];
  rawLevels: Level[] = [];
  volume = 1;
  speed = 1;
  backupVolume = 1;
  readonly status = signal<'idle' | 'loading' | 'playing'>('idle');
  @ViewChild('video') video: ElementRef<HTMLVideoElement>;
  @ViewChild('pastPlayed') pastPlayed: ElementRef<HTMLDivElement>;
  @Input({ required: true }) active: boolean;
  @Input({ required: true }) targetWidth: number;
  @Input({ required: true }) videoData: {
    width: number;
    height: number;
    frame_rate?: string;
    duration?: number;
    frame_count?: number;
  };
  @Input({ required: true }) uuid: string;
  @Input() origin: string;
  @Input({ required: true }) poster: string;
  @Input() assetVersion: AssetVersionDto;
  @Input() asset: AssetDto;
  @Input() gallery: GalleryDto;
  @Input() galleryPermissions: GalleryDto['permissions'];
  @Input() isTeamMember: boolean;
  @Input() isFocused: boolean;
  @Input() autoplay = false;
  @Input() showUi = true;
  @Input() handleUi: boolean;
  @Input() muted: boolean;
  @Input() allowUnmute: boolean;
  @Input() loop: boolean;
  @Input() reportTimeChange: boolean;
  @Input() handleKeyboardShortcuts = false;
  @Input() cover = false;
  @Input() contain = false;
  @Input() blockVideoClick = false;
  @Input() scale = 1;
  @Input() coverPosition: string;
  @Input() activateOnClick: boolean;
  @Input() selectedThreadId: string;
  @Input() showResolved: boolean;
  @Input() commentService: CommentService;
  @Output() onTimeChange = new EventEmitter<VideoTimeData>();
  @Output() selectThread = new EventEmitter<CommentThread>();
  buffered: { start: number; end: number }[] = [];
  speeds = [0.2, 0.5, 0.8, 1, 1.2, 1.5, 2];
  showPlayPauseIndicator = false;
  showTimeSkipIndicator = false;
  showSpeedIndicator = false;
  showMuteIndicator = false;
  initted = false;
  loadedSource = false;
  currentLevel: number;
  videoTimeSub: Subscription;
  lastTimeSkipDirection: 'back' | 'forward' = 'back';
  currentHoverTime: number;
  currentHoverPosition: number = null;
  mouseDownOnTimeline = false;
  commentFace$: Observable<CommentThread[]>;
  potentiallyDraggingThread: CommentThread;
  draggingThread: CommentThread;
  draggingStartPosition: Rect;
  hlsSupported: boolean;
  isMobile = ScreenSizes.screenSize() === 'mobile';

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.autoPickLevel();
    this.isMobile = ScreenSizes.screenSize() === 'mobile';
  }

  @HostListener('window:keydown', ['$event'])
  keyDownEvent(event: KeyboardEvent) {
    if (!this.handleKeyboardShortcuts) return;
    if (event.target['classList'].contains('textarea')) return;
    if (event.target['tagName'] === 'INPUT') return;
    if (event.target['tagName'] === 'TEXTAREA') return;

    const metaKey = isMetaKey(event);
    if (event.code === 'Space') {
      event.preventDefault();
      if (!this.active) this.initialPlayClick();
      else this.togglePlay();
      this.analytics.reportKeyboardShortcut('play-pause-video', 'asset-page');
      this.handlePlayPauseIndicator();
    }

    if (event.code === 'ArrowLeft') {
      if (metaKey) {
        event.preventDefault();
        this.moveFrames(-1);
      } else if (this.isFocused) {
        event.preventDefault();
        this.moveSeconds(-5);
        this.handleSkipTimeIndicator('back');
      }
    }

    if (event.code === 'ArrowRight' && this.isFocused) {
      event.preventDefault();
      if (metaKey) {
        this.moveFrames(1);
      } else {
        this.moveSeconds(5);
        this.handleSkipTimeIndicator('forward');
      }
    }

    if (event.code === 'KeyM') {
      event.preventDefault();
      this.toggleMute();
      this.analytics.reportKeyboardShortcut('toggle-mute', 'video-viewer');
      this.handleMuteIndicator();
    }

    if (event.code === 'KeyS') {
      event.preventDefault();
      this.togglePlaybackSpeed();
      this.analytics.reportKeyboardShortcut('toggle-playback-speed', 'video-viewer');
    }
  }

  ngOnInit(): void {
    this.hlsSupported = Hls.isSupported();
    this.videoTimeSub = this.videoTime.onTimeChange.subscribe(async data => {
      if (!data) return;
      if (data.type === 'frame-change') {
        if (!this.active) return;
        this.moveFrames(data.amount);
      }
      if (data.type === 'time-set') {
        if (!this.active) await this.initialPlayClick();
        if (!this.video?.nativeElement.paused) this.pause();
        this.updateCurrentTime(data.amount);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    if ((changes.asset || changes.showResolved) && this.gallery && this.commentService) {
      const loadComments = this.showResolved || this.asset.unresolved_comments_count > 0;
      this.commentFace$ = loadComments
        ? this.commentService.getComments(this.asset).pipe(
            map(comments =>
              this.commentService.filterCommentsForAccessLevel(comments, this.galleryPermissions, this.isTeamMember)
            ),
            map(comments =>
              (comments as CommentThread[]).filter(
                comment =>
                  !comment.parent_comment && comment.position?.frame !== undefined && comment.position?.frame !== null
              )
            )
          )
        : of([]);
    }

    if (changes.active && this.active) {
      await this.activate();
    }
    if (this.hls) {
      if (changes.autoplay) {
        if (this.video.nativeElement.paused && this.autoplay) this.play();
        else if (!this.video.nativeElement.paused && !this.autoplay) this.pause();
      }
      if (changes.active && this.active && !this.loadedSource) {
        this.loadedSource = true;
        this.hls.startLoad();
      }
      if (changes.active && !this.active && this.loadedSource) {
        this.loadedSource = false;
        if (!this.hls.forceStartLoad) {
          this.hls.stopLoad();
        }
        this.pause();
      }
    }
    await sleep(0);
    this.sendTimeData();
  }

  private async activate() {
    await this.init();
  }

  async initialPlayClick() {
    this.active = true;
    await this.activate();
    this.play();
  }

  async init() {
    if (this.initted) return;
    this.initted = true;

    if (!this.assetVersion && !this.origin) return;
    if (!this.uuid) return; // old cover videos don't have uuids

    this.hls = new Hls({ autoStartLoad: false, maxBufferSize: 0, maxBufferLength: 9, startPosition: 0 });
    const url = this.videoHlsService.getHlsUrl(
      this.uuid,
      this.origin ?? this.assetVersion.original_key,
      this.videoData.width,
      this.videoData.height,
      this.videoData.duration
    );
    this.status.set('loading');
    await this.videoHlsService.loadHls(url, () => !this.hls);

    if (!Hls.isSupported()) {
      this.video.nativeElement.src = url;
      this.loadedSource = true;
      this.status.set('idle');
      this.active = true;
      return;
    }

    if (!this.hls || !this.video) return;
    this.hls.loadSource(url);
    if (this.active) {
      this.loadedSource = true;
      this.hls.startLoad();
    }
    this.hls.attachMedia(this.video.nativeElement);

    if (!this.muted) {
      this.volume = this.videoSettingsService.videoSettings().volume;
      this.adjustVolume();
    }
    if (this.showUi) {
      this.adjustSpeed(this.videoSettingsService.videoSettings().speed);
    }
    this.hls.on(Hls.Events.MANIFEST_LOADED, (event, data) => {
      this.levels = data.levels;
      if (this.autoplay) {
        this.play();
      }
      this.zone.runOutsideAngular(() => {
        this.updateTimelineWidth();
      });
    });
    this.sendTimeData();
    this.hls.on(Hls.Events.MANIFEST_PARSED, (event, data) => {
      this.rawLevels = data.levels;
      this.autoPickLevel();
    });
    this.hls.on(Hls.Events.FRAG_LOADED, () => {
      this.calcBuffered();
    });
  }

  private clickStart = 0;

  videoClickStart() {
    this.clickStart = Date.now();
  }

  videoClick() {
    if (this.blockVideoClick) return;
    if (Date.now() - this.clickStart > longPressDuration) return;
    this.togglePlay();
    this.handlePlayPauseIndicator();
  }

  private autoPickLevel() {
    if (!this.hls) return;
    let level = this.rawLevels.findIndex(level => {
      return level.width >= this.targetWidth * devicePixelRatio();
    });
    if (level === -1) level = this.rawLevels.length - 1;
    this.hls.currentLevel = level;
    this.currentLevel = level;
  }

  playPauseClick(event: MouseEvent) {
    event.stopPropagation();
    this.togglePlay();
  }

  togglePlay() {
    if (this.video.nativeElement.paused) {
      this.play();
    } else {
      this.pause();
    }
  }

  async play() {
    this.status.set('loading');
    this.check.detectChanges();
    this.video.nativeElement.playsInline = true;
    try {
      await this.video.nativeElement.play();
      this.status.set('playing');
    } catch (err) {
      console.debug(err);
      this.status.set('idle');
    }
    this.check.detectChanges();
  }

  pause(retry = false) {
    if (!this.video && !retry) {
      setTimeout(() => this.pause(true), 10);
      return;
    }
    this.video.nativeElement.pause();
    this.status.set('idle');
    this.check.detectChanges();
  }

  private async handlePlayPauseIndicator() {
    this.showPlayPauseIndicator = false;
    this.check.detectChanges();
    await sleep(0);
    this.showPlayPauseIndicator = true;
    this.check.detectChanges();
    await sleep(0);
    this.showPlayPauseIndicator = false;
    this.check.detectChanges();
  }

  private async handleSkipTimeIndicator(direction: 'back' | 'forward') {
    this.lastTimeSkipDirection = direction;
    this.showTimeSkipIndicator = false;
    this.check.detectChanges();
    await sleep(0);
    this.showTimeSkipIndicator = true;
    this.check.detectChanges();
    await sleep(0);
    this.showTimeSkipIndicator = false;
    this.check.detectChanges();
  }

  private async handleSpeedIndicator() {
    this.showSpeedIndicator = false;
    this.check.detectChanges();
    await sleep(0);
    this.showSpeedIndicator = true;
    this.check.detectChanges();
    await sleep(0);
    this.showSpeedIndicator = false;
    this.check.detectChanges();
  }

  private async handleMuteIndicator() {
    this.showMuteIndicator = false;
    this.check.detectChanges();
    await sleep(0);
    this.showMuteIndicator = true;
    this.check.detectChanges();
    await sleep(0);
    this.showMuteIndicator = false;
    this.check.detectChanges();
  }

  loadLevel(level: number) {
    if (!this.hls) return;
    this.hls.currentLevel = level;
    this.currentLevel = level;
  }

  adjustVolume() {
    this.video.nativeElement.volume = this.volume;
    if (this.volume === 0 && !this.muted) this.mute();
    else if (this.muted) {
      this.backupVolume = this.volume;
      this.unmute();
    }
    this.videoSettingsService.videoSettings.set({
      ...this.videoSettingsService.videoSettings(),
      volume: this.volume,
    });
  }

  adjustSpeed(speed: number) {
    this.speed = speed;
    this.video.nativeElement.playbackRate = this.speed;
    this.videoSettingsService.videoSettings.set({
      ...this.videoSettingsService.videoSettings(),
      speed: this.speed,
    });
  }

  togglePlaybackSpeed() {
    const index = this.speeds.findIndex(speed => speed === this.speed);
    const nextIndex = (index + 1) % this.speeds.length;
    this.adjustSpeed(this.speeds[nextIndex]);
    this.handleSpeedIndicator();
  }

  toggleMute() {
    if (this.muted) {
      this.unmute();
    } else this.mute();
  }

  toggleMutePreventDefault(event: MouseEvent) {
    event.stopPropagation();
    event.preventDefault();
    this.toggleMute();
  }

  mute() {
    this.muted = true;
    this.video.nativeElement.muted = this.muted;
    if (this.volume) this.backupVolume = this.volume;
    this.volume = 0;
    this.video.nativeElement.volume = this.volume;
    this.videoSettingsService.videoSettings.set({
      ...this.videoSettingsService.videoSettings(),
      volume: this.volume,
    });
  }

  unmute() {
    this.muted = false;
    this.video.nativeElement.muted = this.muted;
    this.volume = this.backupVolume;
    this.video.nativeElement.volume = this.volume;
    this.videoSettingsService.videoSettings.set({
      ...this.videoSettingsService.videoSettings(),
      volume: this.volume,
    });
  }

  private allowDrag(thread: CommentThread) {
    return false; // disabling this for now. Need to figure out how to handle dragging (only while editing, etc.)
    if (this.galleryPermissions.can_edit) return true;
    const userLike = thread.user || thread.guest;
    const currentUser = this.state.user || this.state.guest;
    return userLike?.id === currentUser?.id;
  }

  threadMouseDown(event: MouseEvent, thread: CommentThread) {
    if (!this.allowDrag(thread)) return;
    this.potentiallyDraggingThread = thread;
    this.draggingStartPosition = Rect.fromMouseEvent(event);
  }

  threadMouseMove(event: MouseEvent) {
    const pos = Rect.fromMouseEvent(event);
    if (this.potentiallyDraggingThread) {
      const diff = pos.subtract(this.draggingStartPosition).magnitude();
      if (diff > 7) {
        this.draggingThread = this.potentiallyDraggingThread;
        this.potentiallyDraggingThread = undefined;
      }
    }

    if (!this.draggingThread) return;
    const percent = this.getPercentFromMouseEvent(event);
    this.draggingThread.position.frame = percent;
  }

  threadMouseUpOrLeave() {
    if (!this.commentService) return;
    if (this.draggingThread) {
      this.commentService
        .updateComment(
          this.asset.id,
          this.draggingThread.id,
          {
            position: this.draggingThread.position,
          },
          this.gallery.id
        )
        .subscribe();
      this.selectThread.emit(undefined);
      this.selectThread.emit(this.draggingThread);
    }
    this.potentiallyDraggingThread = undefined;
    this.draggingThread = undefined;
  }

  mousedown(event: MouseEvent) {
    this.setCurrentTimeFromClick(event);
    this.mouseDownOnTimeline = true;
  }

  mousemove(event: MouseEvent) {
    event.stopPropagation();
    this.setCurrentTimeFromClick(event);
  }

  touchTimelineInteraction(event: Event) {
    event.stopPropagation();
    event.preventDefault();
  }

  private getPercentFromMouseEvent(event: MouseEvent) {
    const currentTarget = this.video.nativeElement;
    const { width, left } = currentTarget.getBoundingClientRect();
    const percent = (event.clientX - left) / width;
    return Math.max(Math.min(percent, 1), 0);
  }

  setCurrentTimeFromClick(event: MouseEvent) {
    const target = event.target as HTMLDivElement;
    const percent = this.getPercentFromMouseEvent(event);
    const time = percent * this.getDuration();
    this.currentHoverTime = time;
    const elBox = target.getBoundingClientRect();
    this.currentHoverPosition = Math.min(Math.max(event.clientX - elBox.left, 0), elBox.width);

    if (event.buttons !== 1) return;
    this.updateCurrentTime(time);
  }

  public updateCurrentTime(time: number) {
    if (!this.video?.nativeElement) return;
    if (time === Infinity || time === -Infinity || isNaN(time)) return;
    this.video.nativeElement.currentTime = time;
    this.updateTimelineWidthActual();
  }

  getDuration() {
    if (this.assetVersion.status === 'completed') {
      return this.video?.nativeElement?.duration || 0;
    }
    const data = this.assetVersion.data as AssetVideoTypeDataDto;
    return data?.duration || 0;
  }

  private calcBuffered() {
    const fragments = this.hls.levels[this.currentLevel].details?.fragments || [];
    if (!fragments?.length) return;
    this.buffered = [];
    const duration = this.getDuration();

    fragments.forEach(fragment => {
      const { start, end, stats } = fragment;
      const loaded = !!stats.loaded;
      if (!loaded) return;

      const buffer = {
        start: (Math.round(start) / duration) * 100,
        end: (Math.round(end) / duration) * 100,
      };

      const combineWith = this.buffered.find(b => b.end === buffer.start || b.start === buffer.end);
      if (combineWith) {
        combineWith.start = Math.min(combineWith.start, buffer.start);
        combineWith.end = Math.max(combineWith.end, buffer.end);
      } else {
        this.buffered.push(buffer);
      }
    });

    this.check.markForCheck();
  }

  getProcessingPieceWidth() {
    if (this.assetVersion.status === 'completed') return 0;
    const data = this.assetVersion.data as AssetVideoTypeDataDto;
    const fullDuration = data?.duration || 0;
    const readyDuration = this.video?.nativeElement?.duration || 0;
    return 100 - (readyDuration / fullDuration) * 100;
  }

  getPastPlayedWidth() {
    const video = this.video?.nativeElement;
    if (!video) return 0;
    const duration = this.getDuration();
    if (!duration) return 0;
    return (video.currentTime / duration) * 100;
  }

  private sendTimeData = throttle(async () => {
    const videoEl = this.video?.nativeElement;
    if (!this.reportTimeChange) return;
    if (!this.hls) return;
    this.onTimeChange.emit({
      currentSecond: videoEl?.currentTime || 0,
      durationSeconds: this.videoData.duration || 0,
      currentFrame: videoEl ? videoEl.currentTime * this.getFrameRate() : 0,
      durationFrames: this.videoData.frame_count || 0,
    });
    await sleep(100);
    this.sendTimeData();
  }, 99);

  private getFrameRate() {
    return this.videoData.frame_rate ? this.convertFrameRate(this.videoData.frame_rate) : 30;
  }

  private convertFrameRate(frameRate: string) {
    if (typeof frameRate === 'number') return Number(frameRate);
    const [numerator, denominator] = frameRate.toString().split('/');
    if (!denominator) return Number(numerator);
    return Math.round(parseInt(numerator) / parseInt(denominator));
  }

  moveFrames(frameCount: number) {
    if (!this.video) return;
    const video = this.video.nativeElement;
    video.currentTime += frameCount / this.getFrameRate();
    this.sendTimeData();
    this.updateTimelineWidthActual();
  }

  moveSeconds(seconds: number) {
    if (!this.video) return;
    const video = this.video.nativeElement;
    video.currentTime += seconds;
    this.sendTimeData();
    this.updateTimelineWidthActual();
  }

  private updateTimelineWidth() {
    if (!this.hls) return;
    const video = this.video.nativeElement;

    this.updateTimelineWidthActual();
    if (video.paused) {
      const onPlay = () => {
        video.removeEventListener('play', onPlay);
        this.updateTimelineWidth();
      };
      video.addEventListener('play', onPlay);
      return;
    }
    window.requestAnimationFrame(() => this.updateTimelineWidth());
  }

  private updateTimelineWidthActual() {
    const video = this.video?.nativeElement;
    if (this.pastPlayed && video) {
      this.pastPlayed.nativeElement.style.width = this.getPastPlayedWidth() + '%';
    }
  }

  async toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      const video = this.video.nativeElement;
      try {
        const rfs =
          video.requestFullscreen ||
          video['webkitEnterFullScreen'] ||
          video['webkitRequestFullScreen'] ||
          video['mozRequestFullScreen'] ||
          video['msRequestFullscreen'];
        await rfs.call(video);
      } catch (err) {
        this.toast.add('Fullscreen request failed: ' + err, 'error');
        console.warn('Fullscreen request failed', err);
      }
    }
  }

  ngOnDestroy() {
    this.hls?.destroy();
    this.hls = undefined;
    this.videoTimeSub?.unsubscribe();
  }
}
